import * as angular from "angular";
import { ITimeZoneInfo } from "../userPreferences/model/ITimeZoneInfo";
import { ICountry } from "@directives/_forms/smsInput/ICountry";
import { IPreferencesContent } from "@interfaces/IPreferencesContent";
import { ICombinedPreferences } from "@interfaces/ICombinedPreferences";

export enum RouteStartCondition {
    JourneyStart,
    DepartFromAnyVisit,
    DepartStartLocation
}

export enum RouteEndCondition {
    ArriveAllVisits,
    ArriveEndLocation
}

export enum VisitMatchOptions {
    None = 0,
    OnLocation = 1,
    OnJourney = 2,
    OnJob = 4,
    OnDwell = 8
}

export enum Section {
    General,
    Map,
    Cost,
    Measurement,
    Internationalisation,
    Tms,
    Report
}

export enum Currency {
    GBP,
    EUR,
    USD
}

export enum JourneyCostType {
    Distance,
    Hourly
}

export enum LandingPageOptions {
    Dashboard,
    Vehicles,
    Tms,
    Compliance,
    Maintenance,
    Geofences,
    Resources
}

export enum DistanceMeasurement {
    Kilometres,
    Miles
}

export enum VolumeMeasurement {
    Litre,
    Gallon,
    UsGallon
}

export enum TemperatureMeasurement {
    Celsius,
    Fahrenheit
}

export enum FuelConsumptionMeasurement {
    MilesPerGallon,
    UnitedStatesMilesPerGallon,
    KilometresPerLitre,
    LitresPer100Kilometres
}

export enum DurationFormat {
    Days,
    DaysHours,
    DaysHoursMinutes,
    DaysHoursMinutesSeconds,
    Hours,
    HoursMinutes,
    HoursMinutesSeconds,
    Minutes,
    MinutesSeconds,
    Seconds
}

export interface IPreferencesQueryOptions {
    AccountId?: number;
    UserId?: number;
    isAccountPreferences?: boolean;
}

export class PreferencesService {

    static $inject = ["$http", "$q", "$timeout"];

    constructor(
        private readonly $http: angular.IHttpService,
        private readonly $q: angular.IQService,
        private readonly $timeout: angular.ITimeoutService
    ) {
    }

    getAllPreferences(): angular.IPromise<ICombinedPreferences> {
        return this
            .$http({
                url: "/api/preferences",
                method: "GET",
            })
            .then((response: angular.IHttpResponse<ICombinedPreferences>) => {
                return response.data;
            });
    }

    getPreferences(queryOptions: IPreferencesQueryOptions): angular.IPromise<IPreferencesContent> {
        return this
            .$http({
                url: "/api/preferences/preferencesContent",
                method: "GET",
                params: queryOptions
            })
            .then((response: angular.IHttpPromiseCallbackArg<IPreferencesContent>) => {
                return response.data;
            });
    }

    getDefaultPreferences(): angular.IPromise<IPreferencesContent> {
        return this
            .$http({
                url: "/api/preferences/default",
                method: "GET"
            })
            .then((response: angular.IHttpPromiseCallbackArg<IPreferencesContent>) => {
                return response.data;
            });
    }

    getAccountPreferences() : angular.IPromise<IPreferencesContent> {
        return this
            .$http({
                url: "/api/preferences/account",
                method: "GET",
            })
            .then((response: angular.IHttpPromiseCallbackArg<IPreferencesContent>) => {
                return response.data;
            });
    }

    updateAccountPreferences(accountPreferences: IPreferencesContent, accountId?: number) : angular.IPromise<void> {
        return this
            .$http({
                url: `/api/preferences/account/${accountId}`,
                method: "POST",
                data: accountPreferences
            }).then(() => { });
    }

    getUserPreferences(): angular.IPromise<IPreferencesContent> {
        return this
            .$http({
                url: "/api/preferences/user",
                method: "GET",
            })
            .then((response: angular.IHttpPromiseCallbackArg<IPreferencesContent>) => {
                return response.data;
            });
    }

    updateUserPreferences(userPreferences: IPreferencesContent): angular.IPromise<void> {
        return this
            .$http({
                url: "/api/preferences/user",
                method: "POST",
                data: userPreferences
            }).then(() => { });
    }

    createNewUserPreferences(userPreferences: IPreferencesContent, userId: number, accountId: number): angular.IPromise<void> {
        const url = `/api/preferences/user/${userId}/account/${accountId}`;
        return this
            .$http({
                url: url,
                method: "POST",
                data: userPreferences
            }).then(() => { });
    }

    getTimezones(sortAsc: boolean = false): angular.IPromise<ITimeZoneInfo[]> {
        return this.$http
            .get("/api/useroptions/timezones/")
            .then((result: angular.IHttpPromiseCallbackArg<ITimeZoneInfo[]>) => {
                if (!sortAsc) {
                    return result.data;
                }

                return result.data.sort((a, b) => {
                    const offsetA = this.parseBaseUtcOffset(a.BaseUtcOffset);
                    const offsetB = this.parseBaseUtcOffset(b.BaseUtcOffset);
                    return offsetA - offsetB;
                });
            });
    }

    getTimezone(timeZoneName: string): angular.IPromise<ITimezoneSetting> {
        return this.$http
            .get(`/api/useroptions/timezone/${timeZoneName}`)
            .then((result: angular.IHttpResponse<ITimezoneSetting>) => {
                return result.data;
            });
    }

    getCountryCodes(): angular.IPromise<ICountry[]> {
        return this.$http
            .get("/api/useroptions/countrycodes/")
            .then((result: angular.IHttpPromiseCallbackArg<ICountry[]>) => {
                return result.data;
            });
    }

    reconcileLanguagesRequiresRefresh(): angular.IPromise<boolean> {
        const localUILanguageString = localStorage.getItem('userSelectedLanguageCode');

        return this.getAllPreferences().then((existingPreferences) => {

            if (!localUILanguageString) {
                // If first time User connects on device - set the local lang instead
                this.updateLocalLanguage(existingPreferences);
                return this.$q.resolve(true); // refresh required
            } else {
                return this.updateDatabasePreferencesIfRequired(existingPreferences, localUILanguageString).then(() => {
                    return this.$q.resolve(false); // no refresh required
                });
            }
        });
    }

    
    private parseBaseUtcOffset(baseUtcOffset: string): number {
        const [hours, minutes, seconds] = baseUtcOffset.split(':').map(Number);
        return hours * 60 + minutes; // Convert to total minutes
    }


    private updateLocalLanguage(existingPreferences: ICombinedPreferences): void {
        let userLangPref = existingPreferences.User?.Language?.Value;
        if (userLangPref == null) {
            userLangPref = "en";
        }

        localStorage.setItem('userSelectedLanguageCode', userLangPref);
    }

    private updateDatabasePreferencesIfRequired(existingPreferences: ICombinedPreferences, LocalUILanguageString: string): angular.IPromise<void> {
        if (!existingPreferences.User) {

            const tempUserPrefs: IPreferencesContent = {} as any;

            existingPreferences.User = tempUserPrefs;
        }

        let saveRequired = false;
        const sanitisedLocalUILanguageString = LocalUILanguageString !== "undefined" ? LocalUILanguageString : "en"

        if (!existingPreferences.User.Language) {
            existingPreferences.User.Language = {
                Value: sanitisedLocalUILanguageString,
                Section: 4,
                savePreference: true
            };
            saveRequired = true;
        }

        if (existingPreferences.User.Language.Value != sanitisedLocalUILanguageString) {
            existingPreferences.User.Language.Value = sanitisedLocalUILanguageString;
            saveRequired = true;
        }

        if (!saveRequired) {
            return this.$q.resolve();
        }

        
        return this.updateUserPreferences(existingPreferences.User).then(() => {
            return this.$timeout(() => { return this.$q.resolve(); }, 1000);
        });
    }
}
